import { AnimatePresence, motion } from "framer-motion"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface Platform {
  name: string
  logo: string
  bgColor: string
}

interface LandingRotatingPlatformCardProps {
  sizeClasses?: string
  interval?: number // Rotation interval after initial hold (ms)
  holdDuration?: number // How long to show Bracket before rotation starts (ms)
  platforms?: Platform[]
}

const DEFAULT_PLATFORMS: Platform[] = [
  {
    name: "X",
    logo: "https://a.bracky.app/assets/icons/platforms/x-app-icon.png",
    bgColor: "#000000",
  },
  {
    name: "Coinbase Wallet",
    logo: "https://a.bracky.app/assets/icons/platforms/coinbase-wallet-app-icon-2.webp",
    bgColor: "#0052FF",
  },
  {
    name: "Farcaster",
    logo: "https://a.bracky.app/assets/icons/platforms/farcaster-app-icon.png",
    bgColor: "#7B61FF",
  },

  {
    name: "World App",
    logo: "https://a.bracky.app/assets/icons/platforms/world-app-icon.png",
    bgColor: "#FFFFFF",
  },
  {
    name: "Telegram",
    logo: "https://a.bracky.app/assets/icons/platforms/telegram-app-icon.png",
    bgColor: "#26A5E4",
  },
  {
    name: "Discord",
    logo: "https://a.bracky.app/assets/icons/platforms/discord-app-icon.png",
    bgColor: "#5865F2",
  },
]

// Bracket logo (shown once at the end of the initial sequence)
const BRACKET_PLATFORM: Platform = {
  name: "Bracket",
  logo: "https://a.bracky.app/assets/icons/platforms/bracket-app-icon-dimensional.png",
  bgColor: "#001CFF",
}

export const LandingRotatingPlatformCard = React.memo(function LandingRotatingPlatformCard({
  sizeClasses = "w-[3.3rem] h-[3.3rem]",
  interval = 1250,
  holdDuration = 2750,
  platforms = DEFAULT_PLATFORMS,
}: LandingRotatingPlatformCardProps) {
  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState<"initial" | "normal">("initial")

  // Track whether the hero section is on screen to pause animations when off-screen
  const [isHeroVisible, setIsHeroVisible] = useState(true)

  // Listen for visibility events dispatched by LandingHero
  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<{ isVisible: boolean }>
      if (typeof custom.detail?.isVisible === "boolean") {
        setIsHeroVisible(custom.detail.isVisible)
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("landingHeroVisible", handler as EventListener)
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("landingHeroVisible", handler as EventListener)
      }
    }
  }, [])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Split out Bracket and non-Bracket platforms; memoized so reference is stable across re-renders
  const nonBracketPlatforms = useMemo(() => platforms.filter((p) => p.name !== BRACKET_PLATFORM.name), [platforms])

  const currentPlatform = phase === "normal" ? nonBracketPlatforms[index] : BRACKET_PLATFORM

  // Utility to clear any existing timer
  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current)
  }

  const clearHoldTimer = () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current)
  }

  /**
   * Convenience helper for dispatching custom DOM events so that other
   * components (e.g. the LIVE word-mark) can react to platform rotation or
   * Bracket-only "trade" moments.
   */
  const dispatchEvent = useCallback((name: string, detail?: Record<string, unknown>) => {
    if (typeof window === "undefined") return
    window.dispatchEvent(new CustomEvent(name, { detail }))
  }, [])

  // Start the appropriate timer based on current phase
  const startTimer = useCallback(() => {
    clearTimer()

    // Rotation through non-Bracket platforms
    timerRef.current = setInterval(() => {
      setIndex((prev) => {
        const next = (prev + 1) % nonBracketPlatforms.length

        // Notify listeners that the card just advanced so they can animate the
        // "green caret button" press.
        dispatchEvent("platformAdvance")

        // Wrapped back to the start → bring Bracket back from bottom & restart sequence
        if (next === 0) {
          clearTimer()
          setPhase("initial")
        }

        return next
      })
    }, interval)
  }, [interval, nonBracketPlatforms.length, dispatchEvent])

  const stopTimer = () => {
    clearTimer()
    clearHoldTimer()
  }

  // Manage phase transitions & timers (respect hero visibility)
  useEffect(() => {
    if (phase === "initial") {
      // Inform listeners that the Bracket logo is now visible so they can
      // trigger hero bursts (stadium lights + caret clusters).
      dispatchEvent("bracketVisible")
      // NEW: Broadcast explicit visibility event used by LandingBackground
      dispatchEvent("bracketLogoVisibility", { isVisible: true })

      // NEW: Kick-off a burst + caret cluster moment (reuse sports event logic)
      dispatchEvent("sportsEmojiClick")

      // Begin a brief "trade frenzy" while the Bracket logo is featured.
      dispatchEvent("tradeActivity", { active: true })

      // Hold the Bracket logo on screen, then resume normal rotation
      holdTimerRef.current = setTimeout(() => {
        // End the trade frenzy before we resume normal rotation
        dispatchEvent("tradeActivity", { active: false })

        // Notify listeners that the Bracket logo is now hidden
        dispatchEvent("bracketLogoVisibility", { isVisible: false })

        // First advance away from Bracket → trigger a green caret press
        dispatchEvent("platformAdvance")

        setPhase("normal")
        setIndex(0)
      }, holdDuration)
    } else if (phase === "normal") {
      // Begin / resume platform rotation **only when hero is visible**
      if (isHeroVisible) startTimer()
    }

    return () => {
      stopTimer()
    }
  }, [phase, holdDuration, startTimer, isHeroVisible])

  // Pause / resume rotation when hero visibility changes
  useEffect(() => {
    if (!isHeroVisible) {
      // Hero off-screen → stop rotation
      clearTimer()
    } else if (phase === "normal") {
      // Hero back on screen → resume if in normal phase
      startTimer()
    }
  }, [isHeroVisible, phase, startTimer])

  const bottomVariants = {
    initial: { y: "100%" },
    animate: { y: "0%" },
    exit: { y: "-100%" },
  }

  const slideDuration = 0.4

  // Determine shimmer intensity based on whether current card is Bracket
  const isBracketPlatform = currentPlatform?.name === BRACKET_PLATFORM.name
  const shimmerOpacity = isBracketPlatform ? 0.64 : 0.32

  return (
    <div className={`relative flex items-center justify-center rounded-xl overflow-hidden ${sizeClasses}`}>
      <AnimatePresence initial={false}>
        <motion.div
          key={`container-${currentPlatform?.name ?? "none"}-${phase}`}
          className="absolute inset-0"
          initial="initial"
          animate="animate"
          exit="exit"
          variants={bottomVariants}
          transition={{ duration: slideDuration, ease: [0.4, 0.0, 0.2, 1] }}
        >
          {/* Platform image */}
          {currentPlatform && (
            <img
              src={currentPlatform.logo}
              alt={currentPlatform.name}
              className="absolute inset-0 w-full h-full object-contain"
            />
          )}

          {/* Shimmer effect – only for non-Bracket platforms when hero is visible */}
          {isHeroVisible && !isBracketPlatform && (
            <motion.div
              className="absolute inset-0 pointer-events-none z-10"
              initial={{ x: "-120%", y: "120%" }}
              animate={{ x: "120%", y: "-120%" }}
              transition={{
                duration: slideDuration * 1.5 + 0.3, // Slowed down by 0.3s
                ease: "easeInOut",
                delay: slideDuration * 0.1, // Small delay so shimmer starts as image becomes visible
              }}
            >
              <div
                className="absolute left-0 top-0 w-[140%] h-[38%]"
                style={{
                  background: "rgba(255,255,255,0.9)",
                  opacity: shimmerOpacity,
                  transform: "rotate(45deg)",
                  filter: "blur(10px)",
                  mixBlendMode: "screen",
                }}
              />
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
})
